// Capability documents tab: every uploaded product capability document, newest first (product title, file, product types, entries read, reading state with live page progress or the reason it failed or has not started, and who uploaded it when); a row opens the document page, and the empty state leads to the upload.
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { FileTextIcon, FileUpIcon } from "lucide-react";
import { useMemo } from "react";

import type { CapabilityDocumentSummary } from "@/api/__generated__";
import { DataTable, dataTableColumnHelper, type DataTableColumn } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { ProblemState } from "@/components/common/ProblemState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { linkClass } from "@/lib/cn";
import { formatNumber, plural } from "@/lib/format";

import { documentTitle } from "./labels";
import { capabilityDocumentsQueryOptions } from "./queries";
import { ReadingStatus, RelativeTime } from "./StatusChips";

const column = dataTableColumnHelper<CapabilityDocumentSummary>();

function columns(region: string): DataTableColumn<CapabilityDocumentSummary>[] {
  return [
    column.accessor((row) => documentTitle(row), {
      id: "title",
      header: "Title",
      cell: ({ row, getValue }) => (
        <Link
          to="/$region/assessments/documents/$capabilityDocUid"
          params={{ region, capabilityDocUid: row.original.capability_doc_uid }}
          className={`${linkClass} line-clamp-2 max-w-[300px] min-w-[200px] font-semibold`}
          tabIndex={-1}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {getValue()}
        </Link>
      ),
    }) as DataTableColumn<CapabilityDocumentSummary>,
    column.accessor("file_name", {
      header: "File",
      cell: (info) => (
        <span className="block max-w-[240px] truncate text-chip text-ink-2" title={info.getValue()}>
          {info.getValue()}
        </span>
      ),
    }) as DataTableColumn<CapabilityDocumentSummary>,
    column.accessor("product_types", {
      header: "Product types",
      cell: (info) => (
        <span className="block max-w-[220px] text-chip text-ink-2">
          {info.getValue().length === 0 ? "–" : info.getValue().join(", ")}
        </span>
      ),
    }) as DataTableColumn<CapabilityDocumentSummary>,
    column.accessor("capability_count", {
      header: "Entries",
      cell: ({ row, getValue }) => (
        <span className="tabular-nums">
          {row.original.status === "ready" ? formatNumber(getValue()) : "–"}
        </span>
      ),
    }) as DataTableColumn<CapabilityDocumentSummary>,
    column.display({
      id: "status",
      header: "Status",
      cell: ({ row }) => <ReadingStatus document={row.original} />,
    }),
    column.accessor("uploaded_at", {
      header: "Uploaded",
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5 text-chip">
          <span className="whitespace-nowrap">{row.original.uploaded_by_name ?? "–"}</span>
          <RelativeTime value={row.original.uploaded_at} className="text-xs text-muted-foreground" />
        </div>
      ),
    }) as DataTableColumn<CapabilityDocumentSummary>,
  ];
}

interface CapabilityDocumentsTableProps {
  region: string;
  onUpload: () => void;
  uploadDisabled: boolean;
}

export function CapabilityDocumentsTable({
  region,
  onUpload,
  uploadDisabled,
}: CapabilityDocumentsTableProps) {
  const documents = useQuery(capabilityDocumentsQueryOptions());
  const navigate = useNavigate();
  const tableColumns = useMemo(() => columns(region), [region]);
  const items = documents.data ?? [];
  const body = (() => {
    if (documents.error !== null) {
      return <ProblemState problem={documents.error} retry={() => void documents.refetch()} />;
    }
    if (documents.isPending) {
      return (
        <div aria-hidden className="flex flex-col gap-2 p-4">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <EmptyState
          icon={FileTextIcon}
          title="No documents yet"
          description="Add a document to compare (PDF). Its entries are read automatically."
          action={
            <Button disabled={uploadDisabled} onClick={onUpload}>
              <FileUpIcon aria-hidden />
              Add document
            </Button>
          }
        />
      );
    }
    return (
      <DataTable
        data={items}
        columns={tableColumns}
        getRowId={(row) => row.capability_doc_uid}
        caption="Documents"
        rowHeight={68}
        onRowClick={(row) => {
          void navigate({
            to: "/$region/assessments/documents/$capabilityDocUid",
            params: { region, capabilityDocUid: row.capability_doc_uid },
          });
        }}
        className="max-h-[calc(100dvh-280px)] min-h-[240px]"
      />
    );
  })();
  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4.5 py-3">
        <p className="m-0 text-chip text-muted-foreground">
          {documents.data === undefined ? " " : plural(items.length, "document")}
        </p>
      </div>
      {body}
    </Card>
  );
}
